import React, { useEffect } from 'react'

import {
    Button,
    CepApprovedIcon,
    CepChecklistIcon,
    CepCreditScorePrimeIcon,
    CepDeclinedIcon,
    StCompleteIcon,
    VsEmailIcon,
    VsPhoneIcon,
    VsPlaceIcon,
} from '@octane/spark'
import NoIcon from 'components/Icons/No.svg'
import SafeCheckReportButtonContainer from 'containers/SafeCheckReportButtonContainer'
import { useFeatureFlagContext } from 'contexts/FeatureFlagContextProvider'
import useUser from 'hooks/useUser'
import { LeadDetail } from 'pages/LeadsPage/types'
import SourceClient from 'state/enums/SourceClient'
import { formatCurrency } from 'utils/formatCurrency'
import formatDate from 'utils/formatDate'
import { formatPhoneNumber } from 'utils/formatPhoneNumber'
import getCopyIntoNewUrl from 'utils/getCopyIntoNewPath'
import {
    pushConvertToAppModalClick,
    pushViewLeadModalImpression,
} from 'utils/gtm-utils'
import joinTextValues from 'utils/joinTextValues'
import relativeDays from 'utils/relativeDays'
import titleCase from 'utils/titleCase'

import { ProductIntent } from '../../../../hooks/useBackendProductIntent'
import getLeadSourceClient from '../../utils/getLeadSourceClient'
import {
    getVehicleDisplayFromCollateral,
    isVehicleNoneSelected,
} from '../../utils/getVehicleDisplay'
import {
    Wrapper,
    Header,
    Title,
    LeadId,
    LeadIdLabel,
    Content,
    ContactInformation,
    ContactRow,
    Card,
    CardLabel,
    CardHeader,
    CardRow,
    CardTitle,
    SSNStatus,
    Capitalize,
} from './LeadQuickView.styled'

export default function LeadQuickView({
    lead,
    productIntent,
}: {
    lead: LeadDetail
    productIntent: ProductIntent | undefined
}): JSX.Element {
    const { user } = useUser()
    const featureFlags = useFeatureFlagContext()
    const isSSNProvided = !!lead.consumer.ssn
    const collateral = lead.collateral

    const shouldShowVehicleDetails =
        typeof collateral !== 'string' &&
        !isVehicleNoneSelected(collateral.make, collateral.model)

    const getStatusIcon = (status: string) => {
        return (
            {
                Prequalified: CepApprovedIcon,
                'Pre-Approved': CepApprovedIcon,
                Declined: CepDeclinedIcon,
            }[status] || CepChecklistIcon
        )
    }

    const StatusIcon = getStatusIcon(lead.status)

    useEffect(() => {
        pushViewLeadModalImpression(lead.id)
    }, [lead.id])

    return (
        <Wrapper>
            <Header>
                <Title data-dd-privacy="mask">
                    {joinTextValues([
                        lead.consumer.firstName?.toLocaleLowerCase(),
                        lead.consumer.lastName?.toLocaleLowerCase(),
                    ])}
                </Title>
                <LeadId>
                    <LeadIdLabel>Lead ID</LeadIdLabel>
                    <span>{lead.id}</span>
                </LeadId>
            </Header>
            <Content>
                <ContactInformation>
                    {lead.isPrime && (
                        <CepCreditScorePrimeIcon width={65} height={65} />
                    )}
                    <ContactRow>
                        <div>
                            <VsPhoneIcon width={22} height={22} />
                        </div>
                        <span data-dd-privacy="mask">
                            {formatPhoneNumber(
                                lead.consumer.mobilePhone ||
                                    lead.consumer.homePhone,
                            )}
                        </span>
                    </ContactRow>
                    <ContactRow>
                        <div>
                            <VsEmailIcon width={22} height={22} />
                        </div>
                        <span data-dd-privacy="mask">
                            {lead.consumer.emailAddress}
                        </span>
                    </ContactRow>
                    <ContactRow>
                        <div>
                            <VsPlaceIcon width={22} height={22} />
                        </div>
                        <span>
                            {joinTextValues(
                                [lead.consumer.city, lead.consumer.state],
                                ', ',
                            )}
                        </span>
                    </ContactRow>
                    <a
                        href={getCopyIntoNewUrl(
                            featureFlags,
                            user?.dealership?.primaryAssetType,
                            lead.id,
                        )}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <Button
                            fullWidth
                            onClick={() => {
                                pushConvertToAppModalClick(lead.id)
                            }}
                        >
                            Convert to App
                        </Button>
                    </a>
                </ContactInformation>
                <Card>
                    <CardHeader>
                        <div>
                            <CardLabel>Decision</CardLabel>
                            <CardTitle>{lead.status}</CardTitle>
                            <SafeCheckReportButtonContainer
                                applicationID={lead.id}
                                applicationUUID={lead.uuid}
                                applicationSource="lead"
                                applicationStatus={lead.status}
                                applicationAssetType={lead.assetType}
                                isPreapprovalApplication={
                                    lead.sourceClient ===
                                    SourceClient.DIRECT_TO_CONSUMER_PREAPPROVAL
                                }
                            />
                        </div>
                        <StatusIcon width={65} height={65} />
                    </CardHeader>
                    <CardRow>
                        <span>Received</span>
                        <span>
                            {relativeDays(
                                new Date(formatDate(lead.createdAt)),
                                new Date(),
                            )}
                        </span>
                    </CardRow>
                    <CardRow>
                        <span>Risk Tier</span>
                        <span>
                            {lead.riskTierLabel ??
                                lead.riskTier ??
                                lead.preapprovalRiskTierName}
                        </span>
                    </CardRow>
                    <CardRow>
                        <span>SSN Provided</span>
                        {isSSNProvided ? (
                            <SSNStatus>
                                <StCompleteIcon width={16} height={16} />
                                Yes
                            </SSNStatus>
                        ) : (
                            <SSNStatus>
                                <img src={NoIcon} alt="No" />
                                No
                            </SSNStatus>
                        )}
                    </CardRow>
                    {productIntent && (
                        <>
                            <CardRow>
                                <span>Has a Trade In</span>
                                {productIntent?.tradeIn ? (
                                    <SSNStatus>
                                        <StCompleteIcon
                                            width={16}
                                            height={16}
                                        />
                                        Yes
                                    </SSNStatus>
                                ) : (
                                    <SSNStatus>
                                        <img src={NoIcon} alt="No" />
                                        No
                                    </SSNStatus>
                                )}
                            </CardRow>
                            <CardRow>
                                <span>Accessories</span>
                                {productIntent?.accessories ? (
                                    <SSNStatus>
                                        <StCompleteIcon
                                            width={16}
                                            height={16}
                                        />
                                        Yes
                                    </SSNStatus>
                                ) : (
                                    <SSNStatus>
                                        <img src={NoIcon} alt="No" />
                                        No
                                    </SSNStatus>
                                )}
                            </CardRow>
                            <CardRow>
                                <span>Insurance</span>
                                {productIntent?.insurance ? (
                                    <SSNStatus>
                                        <StCompleteIcon
                                            width={16}
                                            height={16}
                                        />
                                        Yes
                                    </SSNStatus>
                                ) : (
                                    <SSNStatus>
                                        <img src={NoIcon} alt="No" />
                                        No
                                    </SSNStatus>
                                )}
                            </CardRow>
                            <CardRow>
                                <span>Warranty / Protection</span>
                                {productIntent?.warranty ? (
                                    <SSNStatus>
                                        <StCompleteIcon
                                            width={16}
                                            height={16}
                                        />
                                        Yes
                                    </SSNStatus>
                                ) : (
                                    <SSNStatus>
                                        <img src={NoIcon} alt="No" />
                                        No
                                    </SSNStatus>
                                )}
                            </CardRow>
                            <CardRow>
                                <span>Trailer</span>
                                {productIntent?.trailer ? (
                                    <SSNStatus>
                                        <StCompleteIcon
                                            width={16}
                                            height={16}
                                        />
                                        Yes
                                    </SSNStatus>
                                ) : (
                                    <SSNStatus>
                                        <img src={NoIcon} alt="No" />
                                        No
                                    </SSNStatus>
                                )}
                            </CardRow>
                        </>
                    )}
                </Card>
                <Card>
                    <CardHeader
                        flexDirection="column"
                        justifyContent="flex-start"
                    >
                        <CardLabel>Requested Vehicle</CardLabel>
                        <CardTitle>
                            {getVehicleDisplayFromCollateral(lead.collateral)}
                        </CardTitle>
                    </CardHeader>
                    <CardRow>
                        <span>Lead Source</span>
                        <span>
                            {getLeadSourceClient(
                                lead.sourceClient,
                                lead.partnerName ?? 'OEM',
                            )}
                        </span>
                    </CardRow>
                    {typeof lead.collateral !== 'string' && (
                        <>
                            {shouldShowVehicleDetails && (
                                <>
                                    <CardRow>
                                        <span>Condition</span>
                                        <Capitalize>
                                            {lead.collateral?.condition
                                                ? titleCase(
                                                      lead.collateral
                                                          ?.condition,
                                                  )
                                                : lead.collateral?.condition}
                                        </Capitalize>
                                    </CardRow>
                                    <CardRow>
                                        <span>MSRP</span>
                                        <span>
                                            {formatCurrency(
                                                lead.collateral?.msrp,
                                            )}
                                        </span>
                                    </CardRow>
                                </>
                            )}
                        </>
                    )}
                </Card>
            </Content>
        </Wrapper>
    )
}
